import React, { useCallback, useEffect, useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { IoPersonCircle } from "react-icons/io5";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import SystemUsers from "../assets/images/system-users.png";
import TransactionCount from "../assets/images/transaction-count.png";
import TotalTransactions from "../assets/images/total-transaction.png";
import RecentTransactions from "../assets/images/recent-transactions.png";
import FeaturedIcon from "../assets/images/featuredIcon.png";
import {
  getGraphDataSandbox,
  getTotalCount,
  getTotalCountSandbox,
  getTransactions,
  graphData,
} from "../services/transaction";
import toast from "react-hot-toast";

import { useAuth } from "../context/AuthContextProvider";
import { Link, useNavigate } from "react-router-dom";
import MigrateModal from "../components/MigrateModal";

import Toggle from "../components/Toggle";
import { getProfileInfo, toggleState } from "../services/settings";
import { useQuery } from "@tanstack/react-query";
import TransactionModal from "./Transactions/TransactionModal";
import BusinessName from "../components/BusinessName";
import { getNotification } from "../services/settings";

const Dashboard = () => {
  // Function to get the current date in the format "YYYY-MM-DD"
  const getCurrentDate = () => {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    const day = String(currentDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Function to get the date a month ago in the format "YYYY-MM-DD"
  const getDateAMonthAgo = () => {
    const currentDate = new Date();
    currentDate.setMonth(currentDate.getMonth() - 1);
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    const day = String(currentDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [transactions, setTransactions] = useState([]);
  const [count, setCount] = useState({});
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startingDate, setStartingDate] = useState("");
  const [endingDate, setEndingDate] = useState("");
  const [scheme, setScheme] = useState(null);
  const [status, setStatus] = useState(null);
  const [notification, setNotification] = useState([]);
  const [data, setData] = useState([]);
  const [transactModal, setTransactModal] = useState(false);
  const [transactInfo, setTransactInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const { user, isModalOpen, logout, setGlobalProfile, globalProfile } =
    useAuth();

  const navigate = useNavigate();

  const [isProduction, setIsProduction] = useState(false);
  //const [currentProfile, setCurrentProfile] = useState(user.profile);

  // const [profileData, setProfileData] = useState();

  // const handleProfileInfo = async () => {
  //   try {
  //     const res = await getProfileInfo();
  //     if (res.data.base64File) {
  //       // Convert binary data to a data URL
  //       const dataUrl = `data:image/jpeg;base64,${res.data.base64File}`;
  //       // setPreviewImage(dataUrl);
  //     }
  //     setProfileData(res.data);
  //   } catch (error) {
  //     if (error.response) {
  //       toast.error(error.response.data.message);
  //     }
  //   }
  // };
  // console.log(profileData);

  const getGraphDataReqFun = globalProfile === 'TEST' ? getGraphDataSandbox : graphData;

  const getGraphData = async () => {
    try {
      const graph = await getGraphDataReqFun();
      // Ensure data is sorted in ascending order by date
      const sortedGraphData = graph.sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );
      setData(sortedGraphData);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
      }
    }
  };
  useEffect(() => {
    setStartDate(null);
    setEndDate(null);
    getGraphData();
  }, [globalProfile]);

  const fetchTransactions = useCallback(async () => {
    try {
      const res = await getTransactions(1, 5, {
        startDate,
        endDate,
        scheme,
        status,
      });
      setTransactions(res?.data?.result);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
      }
    }
  }, [startDate, endDate, scheme, status]);

  const getTotalCountReqFun = globalProfile === 'TEST' ? getTotalCountSandbox : getTotalCount;
  const fetchTotalCount = useCallback(async () => {
    try {
      const res = await getTotalCountReqFun({
        startingDate,
        endingDate,
        scheme,
        status,
      });
      setCount(res?.data);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
      }
    }
  }, [startingDate, endingDate, scheme, status]);

  useEffect(() => {
    fetchTransactions();
    // handleProfileInfo();
  }, [fetchTransactions, globalProfile]);

  // Effect to set the current date and time when the component mounts
  useEffect(() => {
    const currentDate = new Date();

    // Set startingDate to the beginning of the day (12:00 AM UTC+1)
    const startOfDay = new Date(currentDate);
    startOfDay.setUTCHours(0, 0, 0, 0); // Add UTC+1 (1:00 AM in UTC = 12:00 AM UTC+1)
    const formattedStartingDate = startOfDay.toISOString();

    // Set endingDate to the end of the day (11:59 PM UTC+1)
    const endOfDay = new Date(currentDate);
    endOfDay.setUTCHours(23, 59, 59, 999); // Add UTC+1 (24:00 = 23:59 PM UTC+1)
    const formattedEndingDate = endOfDay.toISOString();

    // Update state with the formatted dates
    setStartingDate(formattedStartingDate);
    setEndingDate(formattedEndingDate);
  }, []);

  useEffect(() => {
    if (startingDate && endingDate) {
      fetchTotalCount();
    }
  }, [startingDate, endingDate, fetchTotalCount, globalProfile]);

  useEffect(() => {
    if (!user.isActivated) {
      navigate("/not-activated");
    }
  });

  const handleProfile = (info) => {
    setTransactModal(true);
    setTransactInfo(info);
  };

  const handleNotification = async () => {
    try {
      const res = await getNotification();
      setNotification(res.data);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
      }
    }
  };

  useEffect(() => {
    handleNotification();
  }, [globalProfile]);

  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };
    const date = new Date(dateString);
    return date.toLocaleString("en-US", options);
  };

  const getCssVar = (name) =>
    getComputedStyle(document.documentElement).getPropertyValue(name).trim();

  const primaryColor = getCssVar("--primary") || "#F58220";

  return (
    <div className="min-h-screen">
      <div className="lg:flex items-center justify-between">
        <h1 className="text-2xl font-bold text-navy-blue">
          Welcome,{" "}
          <span className="">
            <BusinessName />
          </span>
        </h1>
        <div className="md:flex md:items-center">
          <div className="bg-white text-gray-400 font-semibold h-[40px] flex items-center gap-2 px-2 w-[120px]">
            <span>{new Date().toLocaleDateString()}</span>
            <span>
              <svg
                className="mt-1"
                width="10"
                height="5"
                viewBox="0 0 10 5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  opacity="0.5"
                  d="M1 0.5L5 4.5L9 0.5"
                  stroke="black"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </div>

          <Toggle />
        </div>
      </div>

      <div className="w-full flex justify-between xl:flex items-start gap-10 mt-2">
        <div className="w-full mt-[1rem] flex justify-between flex-col xl:w-4/5 pr-4 md:pr-0">
          <div className="xl:grid xl:grid-cols-4 xl:gap-4 md:grid md:grid-cols-2 md:gap-2 lg:flex lg:items-center lg:justify-between space-y-3 md:space-y-0">
            <div className="bg-white shadow-2xl shadow-slate-100 3xl:min-w-[250px] h-[116px] rounded-[10px] flex items-center gap-2 px-8">
              <img
                src={TotalTransactions}
                alt="system-users"
                className="w-[60px] h-[60px]"
              />
              <div className="">
                <p className="font-extrabold text-[22px] text-navy-blue/70">
                  {count?.totalTransactionAmount?.toLocaleString()}
                </p>
                <p className="font-normal text-sm text-navy-blue/70 w-[50px]">
                  Total Transaction Amount
                </p>
              </div>
            </div>

            <div className="bg-white shadow-2xl shadow-slate-100 3xl:min-w-[250px] h-[116px] rounded-[10px] flex items-center gap-2 px-7">
              <img
                src={TransactionCount}
                alt="system-users"
                className="w-[60px] h-[60px]"
              />
              <div>
                <p className="font-extrabold text-[22px] text-navy-blue/70">
                  {count?.transactionCount}
                </p>
                <p className="font-normal text-sm text-navy-blue/70 w-[50px]">
                  Total Transaction Count
                </p>
              </div>
            </div>

            <div className="bg-white shadow-2xl shadow-slate-100  3xl:min-w-[250px] h-[116px] rounded-[10px] flex items-center gap-2 px-7">
              <img
                src={RecentTransactions}
                alt="system-users"
                className="w-[60px] h-[60px]"
              />
              <div>
                <p className="font-extrabold text-[22px] text-navy-blue/70">
                  {count?.approvedTransactionCount}
                </p>
                <p className="font-normal text-sm text-navy-blue/70 w-[50px]">
                  Approved Transaction Account
                </p>
              </div>
            </div>

            <div className="bg-white shadow-2xl shadow-slate-100 3xl:min-w-[250px] h-[116px] rounded-[10px] flex items-center gap-2 px-7">
              <img
                src={SystemUsers}
                alt="system-users"
                className="w-[60px] h-[60px]"
              />
              <div>
                <p className="font-extrabold text-[22px] text-navy-blue/70">
                  {count?.userCount}
                </p>
                <p className="font-normal text-sm text-navy-blue/70 w-[50px] h-[60px]">
                  System Users
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white h-[420px] mt-4 p-5 rounded-md hidden lg:block">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <p className="border-r pr-4 text-sm font-bold text-black-shade">
                  Transaction Analytics
                </p>
              </div>
            </div>

            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                width={500}
                height={400}
                data={data}
                margin={{
                  top: 10,
                  right: 30,
                  left: 0,
                  bottom: 0,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis dataKey="amount" />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="amount"
                  stroke={primaryColor}
                  fill={primaryColor}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="mt-4 w-full xl:w-[20%] xl:block hidden">
          <div className="bg-white w-[100%] h-[550px] xl:overflow-auto px-4 py-2 rounded-md">
            <div className="flex items-center justify-between">
              <p>Notifications</p>
              <span>
                <svg
                  width="14"
                  height="4"
                  viewBox="0 0 14 4"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g opacity="0.3">
                    <path
                      d="M13.4874 0.774726C12.804 0.228664 11.696 0.228664 11.0126 0.774726C10.3291 1.32079 10.3291 2.20613 11.0126 2.7522C11.696 3.29826 12.804 3.29826 13.4874 2.7522C14.1709 2.20616 14.1709 1.32081 13.4874 0.774726Z"
                      fill="#030229"
                    />
                    <path
                      d="M8.23744 0.774726C7.55402 0.228664 6.44598 0.228664 5.76257 0.774726C5.07915 1.32079 5.07915 2.20613 5.76257 2.7522C6.44598 3.29826 7.55402 3.29826 8.23744 2.7522C8.92085 2.20616 8.92085 1.32081 8.23744 0.774726Z"
                      fill="#030229"
                    />
                    <path
                      d="M2.98744 0.774726C2.30402 0.228664 1.19598 0.228664 0.512564 0.774726C-0.170852 1.32079 -0.170852 2.20613 0.512564 2.7522C1.19598 3.29826 2.30402 3.29826 2.98744 2.7522C3.67085 2.20616 3.67085 1.32081 2.98744 0.774726Z"
                      fill="#030229"
                    />
                  </g>
                </svg>
              </span>
            </div>

            {notification?.map((n) => (
              <div key={n.id} className="flex mt-3 gap-2">
                <img src={FeaturedIcon} alt="icon" className="w-6 h-6" />
                <div className="flex flex-col gap-1">
                  <p className="text-[13px] font-bold text-black-shade">
                    {n.subject}
                  </p>
                  <p className="text-[13px] font-normal text-black-shade">
                    {n.to}
                  </p>

                  <p className="text-[12px] font-normal text-gray-400">
                    {formatDate(n.dateCreated)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 bg-white w-[94%] lg:w-full md:px-12 py-5">
        <div className="md:flex items-center justify-between">
          <div className="flex items-center">
            <p className="border-r pr-4 text-sm font-bold text-black-shade">
              Recent Transactions
            </p>
            <div className="flex items-center border-r px-4">
              <div className="flex items-center gap-1 mr-2">
                <div className="rounded-full bg-green-400 w-[6px] h-[6px]"></div>
                <p
                  className="text-[12px] font-normal text-black-shade cursor-pointer"
                  onClick={() => setStatus("APPROVED")}
                >
                  Approved
                </p>
              </div>
              <div className="flex items-center gap-1 mr-2">
                <div className="rounded-full bg-red-400 w-[6px] h-[6px]"></div>
                <p
                  className="text-[12px] font-normal text-black-shade cursor-pointer"
                  onClick={() => setStatus("Declined")}
                >
                  Declined
                </p>
              </div>
            </div>
          </div>
          <span>
            <div className="flex items-center gap-1">
              <button
                className="text-black bg-white border font-semibold w-[80px] h-[38px] rounded-[10px]"
                onClick={() => setStatus(null)}
              >
                All
              </button>
            </div>
          </span>
        </div>
        <div className="mt-4">
          <DataTable
            value={transactions?.slice(0, 5) || []}
            emptyMessage="No recent transactions found."
            onRowClick={(e) => handleProfile(e.data)}
            rowClassName={() => "cursor-pointer hover:bg-gray-50 transition-colors"}
            className="p-datatable-sm"
            responsiveLayout="scroll"
          >
            <Column
              field="id"
              header="Order ID"
              body={(row) => <div className="py-2">{row.id}</div>}
            />
            <Column
              field="description"
              header="Description"
              body={(row) => <div className="py-2">{row.description}</div>}
            />
            <Column
              field="amount"
              header="Amount"
              body={(row) =>
                row.amount != null ? Number(row.amount).toFixed(2) : "-"
              }
            />
            <Column field="currency" header="Currency" />
            <Column
              field="initiated"
              header="Transaction Date"
              body={(row) =>
                row.initiated
                  ? new Date(row.initiated).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })
                  : "-"
              }
            />
            <Column
              field="status"
              header="Status"
              body={(row) => {
                if (!row.status) return "-";
                const s = row.status.toLowerCase();
                const colorClass =
                  s === "approved"
                    ? "text-green-500 font-medium"
                    : s === "declined"
                    ? "text-red-500 font-medium"
                    : "text-gray-600";
                return (
                  <span className={colorClass}>
                    {row.status.charAt(0).toUpperCase() +
                      row.status.slice(1).toLowerCase()}
                  </span>
                );
              }}
            />
            <Column
              field="pan"
              header="Masked PAN"
              body={(row) => <div className="py-2">{row.pan}</div>}
            />
            <Column field="scheme" header="Scheme" />
            <Column
              field="customerName"
              header="Customer Name"
              body={(row) => (
                <div className="py-2 text-center">{row.customerName || "N/A"}</div>
              )}
            />
            <Column
              field="referenceId"
              header="Reference ID"
              body={(row) => <div className="py-2">{row.referenceId}</div>}
            />
          </DataTable>
        </div>
      </div>

      {isModalOpen && <MigrateModal />}
      {transactModal && (
        <TransactionModal
          setIsModalOpen={setTransactModal}
          transactData={transactInfo}
        />
      )}
    </div>
  );
};

export default Dashboard;
